import axios from "axios";
import { REST_API_URL } from "../config";
import { clearStorage, getToken, saveToken } from "../../utils/localStorage";
import { safeLogError } from "../../utils/errorLogger";
import { authEndpoints } from "../auth/authEndpoints";
import i18n from "i18next";
export const smartAxios = axios.create();
let refreshRequest: Promise<string> | null = null;

const refreshAccessToken = async (): Promise<string> => {
  if (!refreshRequest) {
    const refreshToken = getToken("refreshToken");
    if (!refreshToken) {
      clearStorage();
      throw new Error("No refresh token found");
    }

    refreshRequest = axios
      .post(
        authEndpoints.refreshToken,
        { refresh: refreshToken },
        { baseURL: REST_API_URL },
      )
      .then((response) => {
        const accessToken = response.data?.access;
        if (!accessToken) {
          throw new Error(
            "Refresh token response does not contain access token",
          );
        }
        saveToken("access", accessToken);
        saveToken("accessToken", accessToken);
        return accessToken;
      })
      .catch((error) => {
        clearStorage();
        throw error;
      })
      .finally(() => {
        refreshRequest = null;
      });
  }

  return refreshRequest;
};

smartAxios.interceptors.request.use(
  (request) => {
    try {
      // Используем baseURL из запроса, если он указан, иначе берем дефолтный
      if (!request.baseURL) {
        request.baseURL = REST_API_URL;
      }
      const token =
        getToken("access") ||
        getToken("accessToken") ||
        sessionStorage.getItem("accessToken");
      if (token) {
        request.headers.Authorization = `Bearer ${token}`;
      } else {
        console.warn("No access token found");
      }
      const currentLang = i18n.language;

      // Приоритет: сохранённая пользователем таймзона (userInfo.time_zone),
      // иначе — таймзона устройства.
      const savedTimeZone = getToken("time_zone");
      const deviceTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
      const timeZone = savedTimeZone || deviceTimeZone;

      request.headers["User-Timezone"] = timeZone;
      request.headers["Accept-Language"] = currentLang;
    } catch (error) {
      // Безопасное логирование ошибки
      safeLogError("Error retrieving token:", error);
    }

    return request;
  },
  (error) => {
    safeLogError("Axios request interceptor error:", error);
    return Promise.reject(error);
  },
);

smartAxios.interceptors.response.use(
  (response) => {
    return response;
  },
  async (error) => {
    const originalRequest = error.config;

    if (
      error.response &&
      error.response.status === 401 &&
      !originalRequest?._retry &&
      originalRequest?.url !== authEndpoints.refreshToken
    ) {
      originalRequest._retry = true;

      try {
        const newAccessToken = await refreshAccessToken();
        originalRequest.headers = originalRequest.headers ?? {};
        originalRequest.headers["Authorization"] = `Bearer ${newAccessToken}`;
        // Повторяем оригинальный запрос
        return smartAxios(originalRequest);
      } catch (refreshError) {
        // Безопасное логирование ошибки
        safeLogError("Token refresh failed:", refreshError);
        // Здесь можно добавить логику для разлогинивания пользователя
        return Promise.reject(refreshError);
      }
    }

    return Promise.reject(error);
  },
);
